import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/ui/PageLayout';
import MetricsCard from './components/MetricsCard';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import PlatformStatusIndicator from '../../components/ui/PlatformStatusIndicator';
import { SkeletonMetricCard, SkeletonChartCard } from '../../components/ui/SkeletonLoader';
import { useToast } from '../../components/ui/Toast';

const RealtimeLeadFeed = React.lazy(() => import('./components/RealtimeLeadFeed'));
const CampaignPerformanceChart = React.lazy(() => import('./components/CampaignPerformanceChart'));
const RecentActivityTimeline = React.lazy(() => import('./components/RecentActivityTimeline'));

const ChartSkeleton = React.memo(() => <SkeletonChartCard />);
const MetricsSkeleton = React.memo(() => <SkeletonMetricCard />);

const DEFAULT_METRICS = [
  { title: 'Total Leads Generated', value: '0', change: 'No leads yet', changeType: 'neutral', icon: 'Users', description: 'Active leads in your pipeline' },
  { title: 'Conversion Rate', value: '0%', change: 'Improving', changeType: 'neutral', icon: 'TrendingUp', description: 'Leads converted to customers' },
  { title: 'Active Campaigns', value: '0', change: 'None active', changeType: 'neutral', icon: 'Send', description: 'Running email & social campaigns' },
  { title: 'Subscription Usage', value: '0%', change: 'Free Plan', changeType: 'neutral', icon: 'BarChart3', description: 'Monthly plan utilization', usagePercent: 0 }
];

const PLATFORM_FEATURES = [
  { name: 'AI Lead Finder', path: '/ai-lead-finder-and-scrubber', icon: 'Search', desc: 'Find & scrub leads with AI' },
  { name: 'AI Person Finder', path: '/ai-person-finder', icon: 'Users', desc: 'Find decision-makers on LinkedIn' },
  { name: 'AI Dialer', path: '/ai-dialer', icon: 'Phone', desc: 'AI-powered calling & coaching' },
  { name: 'GoHighLevel', path: '/gohighlevel-integration', icon: 'Globe', desc: 'CRM sync & pipeline management' },
  { name: 'Integration Hub', path: '/cross-platform-integration-hub', icon: 'Link', desc: 'Cross-platform automation' },
  { name: 'Workflow Designer', path: '/automation-workflow-designer', icon: 'Zap', desc: 'Visual automation builder' },
];

const QUICK_ACTIONS = [
  { title: 'Discover New Leads', description: 'Use AI to find qualified prospects', icon: 'Search', path: '/ai-lead-finder-and-scrubber' },
  { title: 'Launch Campaign', description: 'Create email or social media campaign', icon: 'Send', path: '/communication-hub' },
  { title: 'Build Sales Funnel', description: 'Create automated lead nurturing', icon: 'GitBranch', path: '/ai-build-marketing-funnels' },
  { title: 'Schedule Follow-ups', description: 'Set reminders for lead outreach', icon: 'Calendar', path: '/calendar-scheduling' }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading, isInitialized } = useAuth();
  const toast = useToast();
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchDashboardMetrics = useCallback(async () => {
    if (!user?.id) {
      setDashboardMetrics(DEFAULT_METRICS);
      setLoading(false);
      return;
    }

    try {
      setError(null);

      const [leadsResult, campaignsResult, subscriptionResult] = await Promise.allSettled([
        supabase?.from('funnel_leads')?.select('id, lead_score, is_converted, created_at')?.eq('user_id', user?.id),
        supabase?.from('communication_campaigns')?.select('id, status, created_at')?.eq('user_id', user?.id),
        supabase?.from('subscriptions')?.select('*, subscription_plans(name, features, amount)')?.eq('user_id', user?.id)?.eq('status', 'active')?.maybeSingle()
      ]);

      const leadsData = leadsResult?.status === 'fulfilled' && !leadsResult?.value?.error
        ? (leadsResult?.value?.data || [])
        : [];
      const campaignsData = campaignsResult?.status === 'fulfilled' && !campaignsResult?.value?.error
        ? (campaignsResult?.value?.data || [])
        : [];
      const subscriptionData = subscriptionResult?.status === 'fulfilled' && !subscriptionResult?.value?.error
        ? subscriptionResult?.value?.data
        : null;

      const totalLeads = leadsData?.length || 0;
      const convertedLeads = leadsData?.filter(l => l?.is_converted)?.length || 0;
      const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100)?.toFixed(1) : '0.0';
      const activeCampaigns = campaignsData?.filter(c => c?.status === 'active')?.length || 0;

      const planFeatures = subscriptionData?.subscription_plans?.features || {};
      const leadsLimit = planFeatures?.leads || 1000;
      const usagePercent = leadsLimit > 0 ? Math.min(Math.round((totalLeads / leadsLimit) * 100), 100) : 0;
      const planName = subscriptionData?.subscription_plans?.name || 'Free Plan';

      setDashboardMetrics([
        {
          title: 'Total Leads Generated',
          value: totalLeads?.toLocaleString(),
          change: totalLeads > 0 ? `+${totalLeads} total` : 'No leads yet',
          changeType: totalLeads > 0 ? 'positive' : 'neutral',
          icon: 'Users',
          description: 'Active leads in your pipeline'
        },
        {
          title: 'Conversion Rate',
          value: `${conversionRate}%`,
          change: parseFloat(conversionRate) > 15 ? 'Above average' : 'Improving',
          changeType: parseFloat(conversionRate) > 15 ? 'positive' : 'neutral',
          icon: 'TrendingUp',
          description: 'Leads converted to customers'
        },
        {
          title: 'Active Campaigns',
          value: activeCampaigns?.toString(),
          change: activeCampaigns > 0 ? `${activeCampaigns} running` : 'None active',
          changeType: activeCampaigns > 0 ? 'positive' : 'neutral',
          icon: 'Send',
          description: 'Running email & social campaigns'
        },
        {
          title: 'Subscription Usage',
          value: `${usagePercent}%`,
          change: planName,
          changeType: usagePercent < 80 ? 'positive' : 'warning',
          icon: 'BarChart3',
          description: 'Monthly plan utilization',
          usagePercent
        }
      ]);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
      setError('Failed to load dashboard metrics');
      setDashboardMetrics(DEFAULT_METRICS);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isInitialized) return;
    if (authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    fetchDashboardMetrics();
  }, [user, authLoading, isInitialized, navigate, fetchDashboardMetrics]);

  const handleQuickAction = useCallback((action) => {
    if (action?.path) navigate(action?.path);
  }, [navigate]);

  const handleFeatureNav = useCallback((path) => {
    if (path) navigate(path);
  }, [navigate]);

  const displayName = React.useMemo(() => {
    return userProfile?.full_name ||
           userProfile?.first_name ||
           user?.user_metadata?.full_name ||
           user?.email?.split('@')?.[0] ||
           'User';
  }, [userProfile, user]);

  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchDashboardMetrics();
    toast?.info('Refreshing dashboard data...', 'Refreshing');
  }, [fetchDashboardMetrics, toast]);

  if (!isInitialized || authLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8 mb-8">
            {[1,2,3,4]?.map(i => <MetricsSkeleton key={i} />)}
          </div>
          <ChartSkeleton />
        </div>
      </PageLayout>
    );
  }

  if (!user) return null;

  const metrics = dashboardMetrics || DEFAULT_METRICS;

  return (
    <PageLayout>
      <div>
        {/* Welcome Header */}
        <div className="bg-card border border-border rounded-xl mb-8 p-6 shadow-elevation-1">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                Welcome back, {displayName}! 👋
              </h1>
              <p className="mt-2 text-muted-foreground">
                Here's an overview of your lead generation activities.
              </p>
              {lastRefreshed && (
                <p className="text-xs text-muted-foreground mt-1">
                  Last updated: {lastRefreshed?.toLocaleTimeString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <PlatformStatusIndicator />
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={handleRetry}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="AlertCircle" size={18} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRetry}>Retry</Button>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          {loading
            ? [1,2,3,4]?.map(i => <MetricsSkeleton key={i} />)
            : metrics?.map((metric, index) => (
                <MetricsCard key={index} {...metric} />
              ))
          }
        </div>

        {/* Quick Actions */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8 shadow-elevation-1">
          <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {QUICK_ACTIONS?.map((action, index) => (
              <button
                key={index}
                onClick={() => handleQuickAction(action)}
                className="flex flex-col items-start p-4 bg-muted/50 hover:bg-muted rounded-xl transition-colors text-left group"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                  <Icon name={action?.icon} size={20} className="text-primary" />
                </div>
                <p className="font-medium text-foreground text-sm">{action?.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{action?.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Platform Features */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8 shadow-elevation-1">
          <h2 className="text-lg font-semibold text-foreground mb-4">Platform Features</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {PLATFORM_FEATURES?.map((feature, index) => (
              <button
                key={index}
                onClick={() => handleFeatureNav(feature?.path)}
                className="flex flex-col items-center p-4 bg-muted/30 hover:bg-muted rounded-xl transition-colors text-center group"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-2 group-hover:bg-primary/20 transition-colors">
                  <Icon name={feature?.icon} size={18} className="text-primary" />
                </div>
                <p className="font-medium text-foreground text-xs">{feature?.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">{feature?.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Charts & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <React.Suspense fallback={<ChartSkeleton />}>
              <CampaignPerformanceChart userId={user?.id} />
            </React.Suspense>
          </div>
          <div className="space-y-6">
            <React.Suspense fallback={<ChartSkeleton />}>
              <RealtimeLeadFeed userId={user?.id} />
            </React.Suspense>
            <React.Suspense fallback={<ChartSkeleton />}>
              <RecentActivityTimeline userId={user?.id} />
            </React.Suspense>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Dashboard;